
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { orderBy, uniqWith } from "lodash";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { DestineringsalternativDTO } from "~app/common/models/planera/v2/DestineringsalternativDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { StringUtils } from "~app/common/utils/string-utils";
import { DestineringsalternativArbetsorderOption } from "~app/features/planera/arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.models";
import { AddArbetsorderIntressentCommand } from "~app/features/planera/commands/AddArbetsorderIntressentCommand";
import { AddManuelltDestineringsalternativCommand } from "~app/features/planera/commands/AddManuelltDestineringsalternativCommand";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ValidationMessageRowComponent } from "../../../../../common/validation/validation-message-row/validation-message-row.component";
import { ITplValidationMessage } from "../tpl-omdestinera/tpl-omdestinera.models";

export interface ITplArbetsordrarDestineringsalternativManuellaResult {
    successArbetsordersIdList: number[];
}
@Component({
    selector: "rask-tpl-arbetsordrar-destineringsalternativ-manuella",
    templateUrl: "./tpl-arbetsordrar-destineringsalternativ-manuella.component.html",
    styleUrls: ["./tpl-arbetsordrar-destineringsalternativ-manuella.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        CheckboxComponent,
        FormsModule,
        ValidationMessageRowComponent,
        ButtonComponent
    ]
})

export class TplArbetsordrarDestineringsalternativManuellaComponent implements OnInit {
    modal = inject(NgbActiveModal);

    data: {
        arbetsorderIdList: number[];
        handelssortimentId: number;
    };

    protected kundorderradHandelssortimentlist: DestineringsalternativArbetsorderOption[];
    protected alternativaHandelssortimentlist: DestineringsalternativArbetsorderOption[];
    protected selectedRow: DestineringsalternativArbetsorderOption = null;
    protected validationMessages: ITplValidationMessage[] = [];
    protected laddadeArbetsordrar = 0;
    private allOptions: DestineringsalternativArbetsorderOption[];

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly commandService = inject(CommandService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);
    private readonly eventHubService = inject(EventHubService);

    async ngOnInit() {
        await this.initList();
    }

    protected selectRow(row: DestineringsalternativArbetsorderOption) {
        // Tömmer felmed..
        this.validationMessages.splice(0);

        if (!this.selectedRow) {
            this.selectedRow = row;
            this.validateRow();
        } else {
            this.selectedRow = null;
        }

    }

    protected save() {

        const arbetsorderList = this.allOptions.filter(x => this.optionRule(x, this.selectedRow));

        arbetsorderList.forEach(ao => {
            const destAltCommand = new AddManuelltDestineringsalternativCommand(
                ao.arbetsorderId,
                ao.handelssortimentId,
                ao.mottagningsplatsId,
                ao.destineringsursprung,
                ao.egenskapskodX,
                ao.overstyrdAvlastningsplats,
                ao.foradlatHandelssortimentId,
                ao.inmatningshandelssortimentId);
            this.commandService.addTransactionCommand(destAltCommand);

            this.eventHubService.registerListener(`arbetsorder/${ao.arbetsorderId.toString()}`);

            const intressentCommand = new AddArbetsorderIntressentCommand((instance) => {
                instance.arbetsorderId = ao.arbetsorderId;
            });
            this.commandService.addTransactionCommand(intressentCommand);
        });

        const worker = this.busyService.addWork(this.commandService.pushCommandQueueTransaction());
        worker.subscribe(_ => {
            this.toastService.add("Ändringar sparade");
            const modalResult: ITplArbetsordrarDestineringsalternativManuellaResult = {
                successArbetsordersIdList: arbetsorderList.map(x => x.arbetsorderId)
            };
            this.modal.close(modalResult);
        });
    }

    protected canSave(): boolean {
        return !!this.selectedRow;
    }

    protected dismiss() {
        this.modal.close();
    }

    private async initList() {
        const optionList: DestineringsalternativArbetsorderOption[] = [];
        for ( const aoid of this.data.arbetsorderIdList) {
            this.laddadeArbetsordrar++;
            const result = await firstValueFrom(this.arbetsorderService.getDestineringsalternativManuella(aoid));

            result.destineringalternativManuella
                .filter(destAlternativ => destAlternativ.handelssortimentId === this.data.handelssortimentId)
                .forEach(destAlternativ => {
                    const destineringsAlternativ = new DestineringsalternativArbetsorderOption(dm => {
                        dm.arbetsorderId = aoid;
                        dm.mottagningsplatsNamn = this.writeMottagningsplats(destAlternativ);
                        dm.handelssortimentNamn = destAlternativ.handelssortimentNamn;
                        dm.handelssortimentId = destAlternativ.handelssortimentId;
                        dm.mottagningsplatsnummer = destAlternativ.mottagningsplatsnummer;
                        dm.mottagningsplatsId = destAlternativ.mottagningsplatsId;
                        dm.destineringsursprung = destAlternativ.destineringsursprung;
                        dm.egenskapskodX = destAlternativ.egenskapskodX;
                        dm.overstyrdAvlastningsplats = destAlternativ.overstyrdAvlastningsplats;
                        dm.foradlatHandelssortimentId = destAlternativ.foradlatHandelssortimentId;
                        dm.inmatningshandelssortimentId = destAlternativ.inmatningshandelssortimentId;
                    });
                    optionList.push(destineringsAlternativ);
                });
        }
        this.allOptions = optionList;

        const uniqeOptions = uniqWith(this.allOptions, this.optionRule);

        const sortedDestineringsalternativ = orderBy(uniqeOptions, m => m.handelssortimentId);

        const setHeaderRow = (list: DestineringsalternativArbetsorderOption[]): DestineringsalternativArbetsorderOption[] => {
            list.forEach(listItem => {
                if (!list.some(y => y.headerRow === listItem.handelssortimentNamn)) {
                    listItem.headerRow = listItem.handelssortimentNamn;
                }
            });
            return list;
        };
        this.alternativaHandelssortimentlist = setHeaderRow(sortedDestineringsalternativ.filter(y => y.isAlternativt) ?? []);
        this.kundorderradHandelssortimentlist = setHeaderRow(sortedDestineringsalternativ.filter(y => !y.isAlternativt) ?? []);
    }

    private optionRule(
        optionA: DestineringsalternativArbetsorderOption,
        optionB: DestineringsalternativArbetsorderOption): boolean {

        return optionA.mottagningsplatsId === optionB.mottagningsplatsId &&
            optionA.isAlternativt === optionB.isAlternativt;
    }

    private validateRow() {
        const antalValdaOptions = this.allOptions.filter(x => this.optionRule(x, this.selectedRow));

        const maxAntal = this.data.arbetsorderIdList.length;
        const valtAntal = antalValdaOptions.length;
        const antalkvar = maxAntal - valtAntal;

        if (antalkvar > 0) {
            this.validationMessages.push({
                text:`${antalkvar} arbetsordrar av ${maxAntal} kan inte uppdateras med valt destineringsalternativ`,
                type:"warning"
            });
        }

    }
    private writeMottagningsplats(alternativ: DestineringsalternativDTO) {
        if (alternativ.foradlatHandelssortimentId == null) {
            return alternativ.mottagningsplatsNamn;
        }

        return StringUtils.suffixString(alternativ.mottagningsplatsNamn, "//FLIS", 20);
    }
}
